import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateShowInviteModal } from "@/redux/meta/metaActions";
import { QUICK_LINKS_CONFIG, PLAY_PAGE_FEATURES, QuickLinkAction } from "@/config/playPageConfig";
import { sanitizeUrl, isInternalUrl, getExternalLinkProps } from "@/utils/urlSanitization";
import { isMobile } from "react-device-detect";
import "./QuickLinks.scss";

/**
 * Configuration for a quick link button
 */
interface QuickLink {
    /** Unique identifier for the link */
    id: string;
    /** Display label for the link */
    label: string;
    /** Icon - can be emoji string or path to PNG image */
    icon: string;
    /** Navigation path or placeholder for action-based links */
    path: string;
    /** Optional special action to perform instead of navigation */
    action?: string;
    /** Whether the link is enabled (defaults to true) */
    enabled?: boolean;
}

/**
 * Props for the QuickLinks component
 */
interface QuickLinksProps {
    /** Array of quick link configurations (defaults to QUICK_LINKS_CONFIG) */
    links?: QuickLink[];
    /** Title to display above the links (defaults to '⚡ Quick Links') */
    title?: string;
    /** Whether to show loading skeleton */
    isLoading?: boolean;
    /** Callback fired when any link is clicked (for analytics) */
    onLinkClick?: (link: QuickLink) => void;
    /** Callback to open rewards drawer (required for rewards action) */
    onRewardsClick?: () => void;
    /** Whether user is on Overwolf platform */
    isOnOverwolf?: boolean;
}

/**
 * QuickLinks Component
 *
 * Displays a grid of quick action buttons for navigation and special actions.
 * Supports both regular navigation and special actions like opening modals/drawers.
 *
 * @example
 * // Basic usage
 * <QuickLinks isLoading={false} />
 *
 * @example
 * // With rewards drawer handler
 * <QuickLinks
 *   onRewardsClick={() => setShowRewardsDrawer(true)}
 *   onLinkClick={(link) => analytics.track('quick_link_clicked', link)}
 * />
 */
const QuickLinks: React.FC<QuickLinksProps> = ({
    links = QUICK_LINKS_CONFIG,
    title = "⚡ Quick Links",
    isLoading = false,
    onLinkClick,
    onRewardsClick,
    isOnOverwolf = false,
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

    // Filter enabled links and apply mobile-specific filtering
    const mobileHiddenIds = ["eat", "rewards", "account"];
    const enabledLinks = useMemo(() => {
        let filteredLinks = links.filter(link => link.enabled !== false);

        // Hide specific links on mobile
        if (isMobile) {
            filteredLinks = filteredLinks.filter(link => !mobileHiddenIds.includes(link.id));
        }

        return filteredLinks;
    }, [links]);

    const displayLinks = PLAY_PAGE_FEATURES.quickLinks.maxItems
        ? enabledLinks.slice(0, PLAY_PAGE_FEATURES.quickLinks.maxItems)
        : enabledLinks;

    const getHoverInfo = (link: QuickLink): string => {
        const hoverMap: Record<string, string> = {
            eat: "Order food and earn rewards",
            shop: "Redeem Fuel for epic loot",
            rewards: "Open LootBoxes for epic rewards",
            invite: "Recruit friends for bonus XP",
            fuel: "Power up with more Fuel",
            account: "Manage your battle profile",
        };
        return hoverMap[link.id] || "Access this feature";
    };

    const handleLinkClick = useCallback((link: QuickLink) => {
    // Custom click handler for analytics or special behavior
        if (onLinkClick) {
            onLinkClick(link);
        }

        // Handle special actions
        if (link.action === QuickLinkAction.OPEN_REWARDS_DRAWER) {
            if (onRewardsClick) {
                onRewardsClick();
            } else {
                // Fallback to navigating to collectibles page
                navigate(sanitizeUrl(link.path));
            }
            return;
        }

        if (link.action === QuickLinkAction.OPEN_INVITE_MODAL) {
            dispatch(updateShowInviteModal(true));
            return;
        }

        // Sanitize URL before navigation
        const safePath = sanitizeUrl(link.path);

        // For external URLs, open in new tab
        if (!isInternalUrl(safePath)) {
            const externalProps = getExternalLinkProps(safePath);
            if (externalProps.href) {
                window.open(externalProps.href, externalProps.target, "noopener,noreferrer");
            }
            return;
        }

        // Default behavior - navigate to the internal path
        navigate(safePath);
    }, [navigate, dispatch, onLinkClick, onRewardsClick]);

    if (isLoading) {
        return (
            <div className="quick-links quick-links--loading">
                <div className="quick-links__skeleton-title" />
                <div className="quick-links__grid">
                    {[...Array(6)].map((_, index) =>
                        <div key={index} className="quick-links__skeleton-item" />)}
                </div>
            </div>
        );
    }

    if (!PLAY_PAGE_FEATURES.quickLinks.enabled || displayLinks.length === 0) {
        return null;
    }

    return (
        <div className="quick-links">
            {!isMobile && <h3 className="quick-links__title">{title}</h3>}
            <div className="quick-links__grid">
                {displayLinks.map((link) =>
                    <button
                        key={link.id}
                        className="quick-link hover-card"
                        onClick={() => handleLinkClick(link)}
                        aria-label={`Navigate to ${link.label}`}
                        data-hover-info={getHoverInfo(link)}
                    >
                        {link.icon.includes(".png") && !imageErrors[link.id]
                            ? <img
                                src={link.icon}
                                alt={link.label}
                                className="quick-link__icon-img"
                                onError={() => {
                                    // Failed to load icon
                                    setImageErrors(prev => ({ ...prev, [link.id]: true }));
                                }}
                            />
                            : <span className="quick-link__icon" role="img" aria-label={link.label}>
                                {imageErrors[link.id] ? "🔗" : link.icon}
                            </span>
                        }
                        <span className="quick-link__label">{link.label}</span>
                    </button>)}
            </div>
        </div>
    );
};

export default React.memo(QuickLinks);